/**
 * FlowPreviewEngine — Pure JS traversal engine for bot flow simulation.
 *
 * Operates entirely on the ReactFlow node/edge format already used by FlowBuilder.
 * No React imports. No API calls. No side effects.
 *
 * Usage:
 *   const engine = new FlowPreviewEngine(nodes, edges);
 *   const startNode = engine.getStartNode();
 *   const outgoing  = engine.getOutgoing(nodeId);      // [{handleId, targetNodeId}]
 *   const nextId    = engine.resolveNext(nodeId, 'opt_1');
 *   const text      = engine.substituteVariables('Hi {{name}}!', { name: 'Alice' });
 */

export const MAX_STEPS = 100;

export class FlowPreviewEngine {
  /**
   * @param {Array} nodes  ReactFlow node objects: { id, type, data: { node_type, is_start, content } }
   * @param {Array} edges  ReactFlow edge objects: { id, source, target, sourceHandle }
   */
  constructor(nodes = [], edges = []) {
    // Indexed for O(1) lookups
    this._nodeMap = new Map(nodes.map((n) => [n.id, n]));

    // Build outgoing adjacency: Map<sourceNodeId, [{handleId, targetNodeId, edgeId}]>
    this._outgoingMap = new Map();
    for (const edge of edges) {
      const handle = edge.sourceHandle || 'default';
      if (!this._outgoingMap.has(edge.source)) {
        this._outgoingMap.set(edge.source, []);
      }
      this._outgoingMap.get(edge.source).push({
        handleId: handle,
        targetNodeId: edge.target,
        edgeId: edge.id,
      });
    }

    this._nodes = nodes;
    this._edges = edges;
  }

  // ─── Node access ──────────────────────────────────────────────────────

  getNode(id) {
    return this._nodeMap.get(id) || null;
  }

  getStartNode() {
    // Prefer explicit is_start flag, fall back to first node in array
    return (
      this._nodes.find((n) => n.data?.is_start) ||
      this._nodes[0] ||
      null
    );
  }

  // ─── Edge traversal ───────────────────────────────────────────────────

  /**
   * Returns all outgoing connections from a node.
   * @param {string} nodeId
   * @returns {Array<{handleId: string, targetNodeId: string, edgeId: string}>}
   */
  getOutgoing(nodeId) {
    return this._outgoingMap.get(nodeId) || [];
  }

  /**
   * Resolve the next node given the handle chosen by the user/engine.
   * Falls back to the first available outgoing edge if `chosenHandle` is not found.
   * Returns null if no outgoing edges exist (dead-end).
   *
   * @param {string} nodeId
   * @param {string} chosenHandle  e.g. 'default', 'success', 'error', 'opt_1'
   * @returns {string|null}  target node id
   */
  resolveNext(nodeId, chosenHandle = 'default') {
    const outgoing = this.getOutgoing(nodeId);
    if (outgoing.length === 0) return null;

    // Exact match first
    const exact = outgoing.find((o) => o.handleId === chosenHandle);
    if (exact) return exact.targetNodeId;

    // For nodes that only have a single 'default' connection, accept any handle choice
    if (outgoing.length === 1) return outgoing[0].targetNodeId;

    return null;
  }

  // ─── Variable substitution ────────────────────────────────────────────

  /**
   * Replace {{varName}} tokens in text with values from the variables map.
   * Unknown tokens are shown as ⟨varName⟩ so the preview is still readable.
   *
   * @param {string} text
   * @param {Object} variables  e.g. { customer_name: 'Alice', order_id: '42' }
   * @returns {string}
   */
  substituteVariables(text = '', variables = {}) {
    return String(text || '').replace(/\{\{([^}]+)\}\}/g, (_, key) => {
      const k = key.trim();
      return Object.prototype.hasOwnProperty.call(variables, k)
        ? String(variables[k])
        : `⟨${k}⟩`;
    });
  }

  // ─── Graph analysis ───────────────────────────────────────────────────

  /**
   * Detect cycles starting from the start node via DFS.
   * Returns a Set of node IDs that are part of at least one cycle.
   */
  detectCycles() {
    const start = this.getStartNode();
    if (!start) return new Set();

    const visited = new Set();
    const inStack = new Set();
    const cycleNodes = new Set();

    const dfs = (id) => {
      if (inStack.has(id)) {
        cycleNodes.add(id);
        return;
      }
      if (visited.has(id)) return;
      visited.add(id);
      inStack.add(id);
      for (const { targetNodeId } of this.getOutgoing(id)) {
        dfs(targetNodeId);
      }
      inStack.delete(id);
    };

    dfs(start.id);
    return cycleNodes;
  }

  /**
   * BFS from start node; returns Set of reachable node IDs.
   * Useful for identifying unreachable nodes in the validator.
   */
  getReachableNodeIds() {
    const start = this.getStartNode();
    if (!start) return new Set();

    const reachable = new Set();
    const queue = [start.id];

    while (queue.length) {
      const id = queue.shift();
      if (reachable.has(id)) continue;
      reachable.add(id);
      for (const { targetNodeId } of this.getOutgoing(id)) {
        if (!reachable.has(targetNodeId)) queue.push(targetNodeId);
      }
    }

    return reachable;
  }

  /**
   * Apply a response_map from an api_call node to a raw response object.
   * response_map format: { variableName: 'path.to.value' }
   * Path supports dot notation and array index (e.g. '0.name', 'items.0.price').
   *
   * @param {Object} responseMap  e.g. { user_name: '0.name' }
   * @param {any}    responseData  the raw JSON returned by the API
   * @returns {Object}  e.g. { user_name: 'Alice' }
   */
  applyResponseMap(responseMap = {}, responseData = null) {
    const result = {};
    for (const [varName, path] of Object.entries(responseMap)) {
      try {
        const parts = String(path || '').split('.');
        let cursor = responseData;
        for (const part of parts) {
          if (cursor === null || cursor === undefined) break;
          cursor = Array.isArray(cursor)
            ? cursor[parseInt(part, 10)]
            : cursor[part];
        }
        result[varName] = cursor !== undefined && cursor !== null ? String(cursor) : '';
      } catch {
        result[varName] = '';
      }
    }
    return result;
  }

  // ─── Introspection ────────────────────────────────────────────────────

  get nodeCount() { return this._nodes.length; }
  get edgeCount() { return this._edges.length; }
}
